/**
 * LandingPage.jsx
 * Editorial, minimal landing page for www.banyanai.co.
 * Deep navy palette, Playfair Display for the wordmark and display type,
 * Inter for body. Designed to feel closer to a financial publication
 * than a typical SaaS marketing site.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, Key, Database } from 'lucide-react';
import {
  ReportIllustration,
  ChatIllustration,
} from '../components/UseCaseIllustrations';

const NAVY = '#0A0A0A';
const NAVY_DEEP = '#000000';
const NAVY_SOFT = '#141414';
const CREAM = '#FFFFFF';
const GOLD = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';
const MUTED = 'rgba(255, 255, 255, 0.70)';
const FAINT = 'rgba(255, 255, 255, 0.45)';

const Wordmark = ({ size = 'text-xl', className = '' }) => (
  <span
    className={`font-display font-medium tracking-tight ${size} ${className}`}
    style={{ color: CREAM }}
  >
    Banyan
  </span>
);

const Hairline = ({ vertical = false, className = '', style = {} }) => (
  <div
    className={className}
    style={{
      background: HAIRLINE,
      width: vertical ? 1 : '100%',
      height: vertical ? '100%' : 1,
      ...style,
    }}
  />
);

const SignInButton = ({ onClick, className = '', large = false }) => (
  <button
    onClick={onClick}
    className={`group relative overflow-hidden font-medium rounded-full transition-all ${
      large ? 'text-[15px] px-7 py-3.5' : 'text-[14px] px-4 py-2'
    } ${className}`}
    style={{ background: CREAM, color: NAVY }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 14px 40px -12px rgba(255,255,255,0.20)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <span className="relative z-10">Sign in</span>
    <span
      aria-hidden
      className={`relative z-10 inline-block ml-2 transition-transform group-hover:translate-x-0.5 ${
        large ? 'group-hover:translate-x-1' : ''
      }`}
    >
      →
    </span>
    {large && (
      <span
        aria-hidden
        className="banyan-sheen absolute top-0 left-0 h-full w-1/3 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
        }}
      />
    )}
  </button>
);

const RequestAccessButton = ({ onClick, className = '', large = false }) => (
  <button
    onClick={onClick}
    className={`font-medium rounded-full transition-all ${
      large ? 'text-[15px] px-7 py-3.5' : 'text-[14px] px-4 py-2'
    } ${className}`}
    style={{
      background: NAVY_DEEP,
      color: CREAM,
      border: `1px solid ${HAIRLINE_STRONG}`,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.borderColor = GOLD;
      e.currentTarget.style.boxShadow = '0 14px 40px -12px rgba(0,0,0,0.40)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.borderColor = HAIRLINE_STRONG;
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    Request access
  </button>
);

const SectionLabel = ({ children }) => (
  <p
    className="text-[12px] font-medium uppercase tracking-[0.18em] mb-4"
    style={{ color: FAINT }}
  >
    {children}
  </p>
);

const USE_CASES = [
  {
    title: 'Report Generation',
    description:
      'Generate portfolio appraisals, performance summaries, and client-ready reports from live custodial data — without manual spreadsheet work.',
    Illustration: ReportIllustration,
    imageAlt: 'Report generation interface',
  },
  {
    title: 'Chatbot Interface',
    description:
      'Ask questions about holdings, performance, and market context in natural language. Banyan grounds every answer in your firm\u2019s actual portfolio data.',
    Illustration: ChatIllustration,
    imageAlt: 'AI chatbot interface',
  },
];

const SECURITY_FEATURES = [
  {
    icon: Lock,
    title: 'No training on your data',
    description:
      'Your portfolio and custodial data are never used to train or fine-tune our models.',
  },
  {
    icon: ShieldCheck,
    title: 'Private data stays private',
    description:
      'Each firm\u2019s data is stored in a dedicated environment, fully isolated from every other client.',
  },
  {
    icon: Key,
    title: 'Modern, secure data practices',
    description:
      'Zero-trust architecture, least-privilege access, and strong authentication at every layer.',
  },
  {
    icon: Database,
    title: 'Encrypted everywhere',
    description:
      'Data is encrypted in transit and at rest — protected across storage, networks, and every touchpoint.',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 12);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleSignIn = () => navigate('/login');
  const handleRequestAccess = () => {
    window.location.href =
      'mailto:hello@banyanai.co?subject=Requesting%20access%20to%20Banyan';
  };

  return (
    <div
      ref={scrollRef}
      className="fixed inset-0 overflow-y-auto font-sans-body"
      style={{ background: NAVY, color: CREAM }}
    >
      {/* Ambient gradients */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background: `radial-gradient(1200px 600px at 85% -10%, rgba(255,255,255,0.03), transparent 60%),
                       radial-gradient(900px 500px at 10% 110%, rgba(255,255,255,0.03), transparent 60%),
                       linear-gradient(180deg, ${NAVY_DEEP} 0%, ${NAVY} 45%, ${NAVY} 100%)`,
        }}
      />
      <div aria-hidden className="banyan-grain fixed inset-0 pointer-events-none" />

      {/* ============ NAVIGATION ============ */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'blur(0px)',
          WebkitBackdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'blur(0px)',
          background: scrolled ? 'rgba(10, 10, 10, 0.85)' : 'transparent',
          borderBottom: scrolled ? `1px solid ${HAIRLINE}` : '1px solid transparent',
        }}
      >
        <div className="mx-auto max-w-[1280px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Wordmark size="text-3xl" />
          <div className="flex items-center gap-3">
            <SignInButton onClick={handleSignIn} />
            <RequestAccessButton onClick={handleRequestAccess} className="hidden sm:inline-flex" />
          </div>
        </div>
      </header>

      <main className="relative z-10">
        {/* ============ HERO ============ */}
        <section className="relative min-h-screen flex flex-col justify-center pt-16 pb-20 md:pb-28">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10 flex flex-col items-center text-center">
            <h1
              className="font-display font-normal banyan-rise banyan-rise-2"
              style={{
                fontSize: 'clamp(2.75rem, 6.2vw, 5.75rem)',
                lineHeight: 1.02,
                letterSpacing: '-0.02em',
                color: CREAM,
                maxWidth: '18ch',
              }}
            >
              Asset management done better, faster, and cheaper.
            </h1>

            <p
              className="mt-10 max-w-xl text-[1.0625rem] leading-relaxed banyan-rise banyan-rise-3"
              style={{ color: MUTED }}
            >
              From the front lines of finance to you.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 banyan-rise banyan-rise-4">
              <SignInButton onClick={handleSignIn} large />
              <RequestAccessButton onClick={handleRequestAccess} large />
            </div>
          </div>
        </section>

        <Hairline />

        {/* ============ USE CASES ============ */}
        <section className="relative py-20 md:py-28">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <SectionLabel>Use cases</SectionLabel>
            <h2
              className="font-display font-normal mb-14 md:mb-16"
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: CREAM,
                maxWidth: '28ch',
              }}
            >
              Two ways teams use Banyan every day.
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {USE_CASES.map(({ title, description, Illustration, imageAlt }) => (
                <div key={title} className="flex flex-col">
                  <div
                    role="img"
                    aria-label={imageAlt}
                    className="relative w-full aspect-[4/3] md:aspect-[16/9] rounded-lg overflow-hidden mb-6"
                    style={{
                      background: NAVY_SOFT,
                      border: `1px solid ${HAIRLINE}`,
                    }}
                  >
                    <Illustration />
                  </div>
                  <h3
                    className="font-display text-xl mb-3"
                    style={{ color: CREAM, letterSpacing: '-0.01em' }}
                  >
                    {title}
                  </h3>
                  <p className="text-[16px] leading-relaxed" style={{ color: MUTED }}>
                    {description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Hairline />

        {/* ============ SECURITY ============ */}
        <section className="relative py-20 md:py-28">
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <SectionLabel>Security</SectionLabel>
            <h2
              className="font-display font-normal mb-5"
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
                color: CREAM,
                maxWidth: '24ch',
              }}
            >
              Your data, protected at every step.
            </h2>
            <p className="text-[16px] leading-relaxed max-w-2xl mb-14 md:mb-16" style={{ color: MUTED }}>
              Banyan is built for firms that handle sensitive financial data. These principles
              guide how we store, access, and protect everything on the platform.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-10 md:gap-y-12">
              {SECURITY_FEATURES.map(({ icon: Icon, title, description }) => (
                <div key={title} className="flex gap-5">
                  <div className="flex-shrink-0 pt-0.5">
                    <Icon size={28} strokeWidth={1.25} style={{ color: CREAM }} />
                  </div>
                  <div>
                    <h3
                      className="font-display text-lg mb-2"
                      style={{ color: CREAM, letterSpacing: '-0.01em' }}
                    >
                      {title}
                    </h3>
                    <p className="text-[16px] leading-relaxed" style={{ color: MUTED }}>
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ FOOTER ============ */}
        <footer className="relative pt-16 pb-10" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
          <div className="mx-auto max-w-[1280px] px-6 md:px-10">
            <div className="flex flex-col items-center gap-8">
              <div className="text-center">
                <Wordmark size="text-2xl" />
                <p className="mt-3 text-[14px]" style={{ color: FAINT }}>
                  Intelligence for modern capital.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
                <a
                  href="mailto:hello@banyanai.co"
                  className="text-[14px] transition-colors"
                  style={{ color: 'rgba(255, 255, 255, 0.60)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.60)')
                  }
                >
                  hello@banyanai.co
                </a>
                <button
                  onClick={handleSignIn}
                  className="text-[14px] transition-colors"
                  style={{ color: 'rgba(255, 255, 255, 0.60)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.60)')
                  }
                >
                  Sign in
                </button>
                <button
                  onClick={handleRequestAccess}
                  className="text-[14px] transition-colors"
                  style={{ color: 'rgba(255, 255, 255, 0.60)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = GOLD)}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = 'rgba(255, 255, 255, 0.60)')
                  }
                >
                  Request access
                </button>
              </div>
            </div>
            <div
              className="mt-12 pt-6 flex flex-col items-center gap-3"
              style={{ borderTop: `1px solid ${HAIRLINE}` }}
            >
              <span className="text-[13px]" style={{ color: 'rgba(255, 255, 255, 0.35)' }}>
                © {new Date().getFullYear()} Banyan AI, Inc. All rights reserved.
              </span>
              <span
                className="text-[13px] font-display italic"
                style={{ color: 'rgba(255, 255, 255, 0.35)' }}
              >
                Made in the USA.
              </span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
